import { useEffect, useMemo, useState } from "react";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import { useAuth } from "../context/AuthContext";
import {
  createSuperAdminBillingCoupon,
  fetchSuperAdminBillingCoupons,
  updateSuperAdminBillingCouponAction,
} from "../api/superadmin";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import "./ReportsSectionCompact.css";

const PAGE_SIZES = [25, 50, 100, 200];

const DEFAULT_FORM = {
  code: "",
  campaign_name: "",
  description: "",
  percent_off: "10",
  duration: "repeating",
  duration_in_months: "3",
  max_redemptions: "",
  max_redemptions_per_business: "1",
  effective_date_local: "",
  effective_time_local: "00:00",
  expires_date_local: "",
  expires_time_local: "23:59",
};

function formatDateTime(value) {
  if (!value) return "--";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function durationLabel(row) {
  if (row.duration === "once") return "Once";
  if (row.duration === "forever") return "Forever";
  const months = Number(row.duration_in_months || 0);
  return months ? `${months} months` : "Repeating";
}

function localWindowLabel(row) {
  const start = row.effective_date_local
    ? `${row.effective_date_local} ${row.effective_time_local || "00:00"}`
    : "Now";
  const end = row.expires_date_local
    ? `${row.expires_date_local} ${row.expires_time_local || "23:59"}`
    : "No expiry";
  return `${start} to ${end}`;
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="text-xs font-semibold uppercase text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-slate-900">{value ?? 0}</div>
    </div>
  );
}

function optionalNumber(value) {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export default function SuperAdminBillingCoupons() {
  const { is_super_admin } = useAuth();
  const [filters, setFilters] = useState({
    status: "active",
    search: "",
    page: 1,
    page_size: 50,
  });
  const [data, setData] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const totalPages = data?.total_pages || 1;
  const items = data?.items || [];
  const queryFilters = useMemo(() => filters, [filters]);

  const loadData = async (nextFilters = queryFilters) => {
    setLoading(true);
    setError("");
    try {
      const payload = await fetchSuperAdminBillingCoupons(nextFilters);
      setData(payload);
    } catch (err) {
      setError(err?.message || "Unable to load billing coupons.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (is_super_admin) loadData(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_super_admin]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
      page: key === "page" ? value : 1,
    }));
  };

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const submitCoupon = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const payload = {
        code: form.code,
        campaign_name: form.campaign_name,
        description: form.description || null,
        percent_off: Number(form.percent_off || 0),
        duration: form.duration,
        duration_in_months:
          form.duration === "repeating" ? optionalNumber(form.duration_in_months) : null,
        max_redemptions: optionalNumber(form.max_redemptions),
        max_redemptions_per_business: optionalNumber(form.max_redemptions_per_business),
        effective_date_local: form.effective_date_local || null,
        effective_time_local: form.effective_date_local ? form.effective_time_local || null : null,
        expires_date_local: form.expires_date_local || null,
        expires_time_local: form.expires_date_local ? form.expires_time_local || null : null,
      };
      await createSuperAdminBillingCoupon(payload);
      setForm(DEFAULT_FORM);
      setNotice("Billing coupon created.");
      await loadData(filters);
    } catch (err) {
      setError(err?.message || "Unable to create billing coupon.");
    } finally {
      setSaving(false);
    }
  };

  const deactivateCoupon = async (row) => {
    const confirmed = window.confirm(`Deactivate coupon ${row.code}? It will stop working for future billing discount entries.`);
    if (!confirmed) return;
    setActingId(row.id);
    setError("");
    setNotice("");
    try {
      await updateSuperAdminBillingCouponAction(row.id, { action: "deactivate" });
      setNotice("Billing coupon deactivated.");
      await loadData(filters);
    } catch (err) {
      setError(err?.message || "Unable to deactivate billing coupon.");
    } finally {
      setActingId(null);
    }
  };

  const goToPage = (page) => {
    const safePage = Math.max(1, Math.min(Number(page || 1), totalPages));
    const next = { ...filters, page: safePage };
    setFilters(next);
    loadData(next);
  };

  if (!is_super_admin) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="overflow-hidden rounded-3xl bg-white shadow-2xl">
          <ReportPageHeader title="Billing Coupons" closeTo="/superadmin" />
          <div className={`space-y-6 px-6 py-6 ${MODAL_TEXT}`}>
            <ReportHelpCard
              title="Help Guide: Billing Coupons"
              subtitle="Create and manage Ellyanna billing discount codes for subscription campaigns."
              sections={[
                {
                  id: "create",
                  title: "Create coupons",
                  description:
                    "Coupons created here are linked to Stripe and can be entered by salon owners on the Billing page.",
                  anchorId: "billing-coupon-create",
                  bullets: [
                    "Effective and expiry date/time are checked using the salon setup timezone when the salon applies the code.",
                    "Use quantity limits to control campaign availability.",
                    "Use one code per campaign so registration success can be tracked clearly.",
                  ],
                },
              ]}
              troubleshooting={[
                {
                  title: "Coupon does not apply",
                  body: "Check active status, date window, quantity limits, and whether the salon has already used the code.",
                },
              ]}
            />

            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            ) : null}
            {notice ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                {notice}
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <SummaryCard label="Active" value={data?.summary?.active} />
              <SummaryCard label="Inactive" value={data?.summary?.inactive} />
              <SummaryCard label="Redemptions" value={data?.summary?.redemptions} />
              <SummaryCard label="Total" value={data?.summary?.total} />
            </div>

            <form id="billing-coupon-create" onSubmit={submitCoupon} className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-slate-900">Create Coupon</h2>
                <p className="text-xs text-slate-500">
                  Date and time limits are evaluated in the applying salon&apos;s setup timezone.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <label className={LABEL_TEXT}>Coupon code</label>
                  <input className={`${FIELD_CLASS} w-full uppercase`} value={form.code} onChange={(event) => updateForm("code", event.target.value.toUpperCase())} placeholder="WELCOME25" required />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Campaign name</label>
                  <input className={`${FIELD_CLASS} w-full`} value={form.campaign_name} onChange={(event) => updateForm("campaign_name", event.target.value)} placeholder="Spring registration" required />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Percent off</label>
                  <input className={`${FIELD_CLASS} w-full`} type="number" min="1" max="100" step="0.01" value={form.percent_off} onChange={(event) => updateForm("percent_off", event.target.value)} required />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Duration</label>
                  <select className={`${FIELD_CLASS} w-full`} value={form.duration} onChange={(event) => updateForm("duration", event.target.value)}>
                    <option value="once">Once</option>
                    <option value="repeating">Repeating</option>
                    <option value="forever">Forever</option>
                  </select>
                </div>
                {form.duration === "repeating" ? (
                  <div>
                    <label className={LABEL_TEXT}>Months</label>
                    <input className={`${FIELD_CLASS} w-full`} type="number" min="1" max="60" value={form.duration_in_months} onChange={(event) => updateForm("duration_in_months", event.target.value)} required />
                  </div>
                ) : null}
                <div>
                  <label className={LABEL_TEXT}>Quantity limit</label>
                  <input className={`${FIELD_CLASS} w-full`} type="number" min="1" value={form.max_redemptions} onChange={(event) => updateForm("max_redemptions", event.target.value)} placeholder="No limit" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Per business limit</label>
                  <input className={`${FIELD_CLASS} w-full`} type="number" min="1" value={form.max_redemptions_per_business} onChange={(event) => updateForm("max_redemptions_per_business", event.target.value)} placeholder="No limit" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Effective date</label>
                  <input className={`${FIELD_CLASS} w-full`} type="date" value={form.effective_date_local} onChange={(event) => updateForm("effective_date_local", event.target.value)} />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Effective time</label>
                  <input className={`${FIELD_CLASS} w-full`} type="time" value={form.effective_time_local} onChange={(event) => updateForm("effective_time_local", event.target.value)} disabled={!form.effective_date_local} />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Expiry date</label>
                  <input className={`${FIELD_CLASS} w-full`} type="date" value={form.expires_date_local} onChange={(event) => updateForm("expires_date_local", event.target.value)} />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Expiry time</label>
                  <input className={`${FIELD_CLASS} w-full`} type="time" value={form.expires_time_local} onChange={(event) => updateForm("expires_time_local", event.target.value)} disabled={!form.expires_date_local} />
                </div>
                <div className="md:col-span-2 xl:col-span-4">
                  <label className={LABEL_TEXT}>Internal description</label>
                  <textarea className={`${FIELD_CLASS} min-h-20 w-full`} value={form.description} onChange={(event) => updateForm("description", event.target.value)} placeholder="Optional notes for campaign tracking" />
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button type="submit" className={BTN_PRIMARY} disabled={saving}>
                  {saving ? "Creating..." : "Create Coupon"}
                </button>
              </div>
            </form>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <div>
                  <label className={LABEL_TEXT}>Status</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.status} onChange={(event) => updateFilter("status", event.target.value)}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="all">All</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className={LABEL_TEXT}>Search</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.search} onChange={(event) => updateFilter("search", event.target.value)} placeholder="Code, campaign, or description" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Rows</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.page_size} onChange={(event) => updateFilter("page_size", Number(event.target.value))}>
                    {PAGE_SIZES.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" className={BTN_PRIMARY} onClick={() => loadData(filters)} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
                <button type="button" className={BTN_OUTLINE} onClick={() => {
                  const next = { status: "active", search: "", page: 1, page_size: 50 };
                  setFilters(next);
                  loadData(next);
                }} disabled={loading}>
                  Clear
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Code</th>
                    <th className="px-3 py-2 text-left">Campaign</th>
                    <th className="px-3 py-2 text-left">Discount</th>
                    <th className="px-3 py-2 text-left">Salon-Time Window</th>
                    <th className="px-3 py-2 text-left">Usage</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td className="px-3 py-4 text-slate-500" colSpan="7">Loading billing coupons...</td></tr>
                  ) : items.length === 0 ? (
                    <tr><td className="px-3 py-4 text-slate-500" colSpan="7">No billing coupons found.</td></tr>
                  ) : (
                    items.map((row) => (
                      <tr key={row.id} className="align-top hover:bg-slate-50">
                        <td className="px-3 py-3">
                          <div className="font-semibold text-slate-900">{row.code}</div>
                          <div className="text-xs text-slate-500">Created {formatDateTime(row.created_at)}</div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="font-semibold text-slate-900">{row.campaign_name}</div>
                          {row.description ? <div className="text-xs text-slate-500">{row.description}</div> : null}
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          <div>{row.percent_off}% off</div>
                          <div className="text-xs text-slate-500">{durationLabel(row)}</div>
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          <div>{localWindowLabel(row)}</div>
                          <div className="text-xs text-slate-500">Uses applying salon setup timezone</div>
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          <div>{row.redemption_count}{row.max_redemptions ? ` / ${row.max_redemptions}` : ""} used</div>
                          <div className="text-xs text-slate-500">Per business: {row.max_redemptions_per_business || "No limit"}</div>
                        </td>
                        <td className="px-3 py-3">
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${row.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                            {row.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex justify-end">
                            {row.is_active ? (
                              <button type="button" className={BTN_OUTLINE} disabled={actingId === row.id} onClick={() => deactivateCoupon(row)}>
                                Deactivate
                              </button>
                            ) : (
                              <span className="text-xs text-slate-500">Deactivated</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm">
              <button type="button" className={BTN_OUTLINE} onClick={() => goToPage(filters.page - 1)} disabled={loading || filters.page <= 1}>Previous</button>
              <div className="text-slate-600">Page {filters.page} of {totalPages}</div>
              <button type="button" className={BTN_OUTLINE} onClick={() => goToPage(filters.page + 1)} disabled={loading || filters.page >= totalPages}>Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
